<template>
  <div class="original-detection-list app-page app-page--fill">
    <el-card class="main-card">
      <!-- 头部工具栏 -->
      <div class="page-toolbar">
        <h2 class="page-title">{{ pageTitle }}</h2>
        <el-button 
          type="primary" 
          circle
          size="small"
          class="app-refresh-btn"
          :icon="Refresh" 
          :loading="loading"
          @click="handleRefresh"
          title="刷新"
        />
      </div>
      
      <div class="table-container">
        <DynamicTable 
          :data="tableData" 
          :model="tableModel"
          :loading="loading"
          :max-height="tableMaxHeight"
          :show-column-selector="true"
          :show-export="true"
          :show-detail-button="canAny('详情', '现场照片下载', '原始数据excel下载', '数据变更')"
          :pagination="paginationConfig"
          :column-labels="columnLabels"
          @detail="handleDetail"
          @sort="handleSort"
          @filter="handleFilter"
          @page-change="handlePageChange"
          @page-size-change="handlePageSizeChange"
          ref="dynamicTableRef"
        />
      </div>
    </el-card>
  </div>
</template>

<script setup lang="ts">
import { ref, reactive, onMounted, onUnmounted, nextTick } from 'vue'
import { ElMessage } from 'element-plus'
import { Refresh } from '@element-plus/icons-vue'
import { useRouter } from 'vue-router'
import { list } from '@/network/index'
import DynamicTable from '@/components/DynamicTable.vue'
import { useDetailStore } from '@/store/detailStore'
import { getMenuChildPathByComponent, getMenuPathByComponent } from '@/utils/menuRoute'
import { useRouteTitle } from '@/utils/useRouteTitle'
import { useButtonPermission } from '@/utils/buttonPermission'

const router = useRouter()
const detailStore = useDetailStore()
const pageTitle = useRouteTitle('原始检测数据列表')
const { canAny } = useButtonPermission('/RawDataManagement/OriginalDetectionList/index.vue')

// 表格引用
const dynamicTableRef = ref<InstanceType<typeof DynamicTable> | null>(null)

// 响应式数据
const loading = ref(false)
const tableData = ref<any[]>([])

// 接口响应数据
const apiResponse = ref<any>(null)

// model数据，用于表格表头展示
const tableModel = ref<Record<string, string> | undefined>(undefined)

// 分页
const currentPage = ref(1)
const pageSize = ref(10)
const total = ref(0)

// 表格最大高度
const tableMaxHeight = ref<number>(500)

// 计算表格最大高度
const calculateTableHeight = () => {
  nextTick(() => {
    const windowHeight = window.innerHeight
    tableMaxHeight.value = windowHeight - 380
  })
}

// 分页配置
const paginationConfig = reactive({
  show: true,
  get page() { return currentPage.value },
  get pageSize() { return pageSize.value },
  get total() { return total.value },
  pageSizes: [10, 20, 50, 100],
  layout: 'total, sizes, prev, pager, next, jumper',
  small: true
})

// 表头字段中文映射
const columnLabels: Record<string, string> = {
  '编码': '编码',
  '名称': '名称',
  '开始时间': '开始时间',
  '最后更新时间': '更新时间',
  '完成时间': '完成时间',
  '提交人员': '提交人员',
  '检验人员': '检验人员',
  '校核人员': '校核人员'
}

// 筛选参数
const filterParams = ref<any[]>([])



// 分页变化
const handlePageChange = (page: number) => {
  currentPage.value = page
  loadData()
}

const handlePageSizeChange = (size: number) => {
  pageSize.value = size
  currentPage.value = 1
  loadData()
}

// 加载数据
const loadData = async () => {
  try {
    loading.value = true
    console.log('发送请求参数:', { page: currentPage.value, intPageSize: pageSize.value })
    console.log('筛选和排序参数:', filterParams.value)
    
    const res = await list.listIndex({ page: currentPage.value, intPageSize: pageSize.value }, filterParams.value)
    
    if (res && res.status === 200 && res.success && res.response && res.response.data) {
      tableData.value = res.response.data
      total.value = res.response.dataCount || res.response.data.length
      apiResponse.value = res
      // 从接口返回中提取model数据
      if (res.response.model && typeof res.response.model === 'object') {
        tableModel.value = res.response.model;
      } else {
        tableModel.value = undefined;
      }
      
      ElMessage.success(`数据加载成功，共 ${res.response.dataCount} 条记录`)
    } else {
      ElMessage.error('数据加载失败')
      tableModel.value = undefined
    }
  } catch (error) {
    console.error('加载数据失败:', error)
    ElMessage.error('数据加载失败')
  } finally {
    loading.value = false
  }
}


// 刷新按钮点击事件
const handleRefresh = () => {
  loadData()
}

// 排序处理函数
const handleSort = (column: string, type: number) => {
  console.log('排序:', column, type === 1 ? '升序' : '降序')
  // 排序参数会通过filter事件一起传递，这里不需要额外处理
  // 因为DynamicTable组件已经将排序和筛选参数合并后通过filter事件发送
}

// 筛选处理函数（现在也处理排序参数）
const handleFilter = (filters: any[]) => {
  console.log('筛选和排序参数:', filters)
  filterParams.value = filters
  currentPage.value = 1 // 重置到第一页
  loadData() // 重新加载数据
}

// 详情按钮点击事件
const handleDetail = (row: any) => {
  console.log('查看详情:', row)
  const listPath = getMenuPathByComponent(
    '/RawDataManagement/OriginalDetectionList/index.vue',
    '/原始数据管理/原始检测数据列表'
  )
  const detailPath = getMenuChildPathByComponent(
    '/RawDataManagement/OriginalDetectionList/index.vue',
    '原始检测数据详情',
    '/原始数据管理/原始检测数据列表/原始检测数据详情'
  )
  
  // 将参数存储到store中
  detailStore.setDetailParams(row.Id, listPath)
  
  // 跳转到详情页面，通过query参数传递ID
  router.push({
    path: detailPath,
    query: { id: row.Id }
  })
}

// 组件挂载时加载数据
onMounted(() => {
  loadData()
  calculateTableHeight()
  window.addEventListener('resize', calculateTableHeight)
})

onUnmounted(() => {
  window.removeEventListener('resize', calculateTableHeight)
})
</script>

<style scoped lang="less">
.original-detection-list {
  flex: 1;
  min-height: 0;

  .main-card {
    flex: 1;
    min-height: 0;
    display: flex;
    flex-direction: column;

    :deep(.el-card__body) {
      flex: 1;
      min-height: 0;
      display: flex;
      flex-direction: column;
      gap: 16px;
    }
  }

  .page-toolbar {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 16px;

    .page-title {
      font-size: 18px;
      font-weight: 600;
      color: #303133;
      margin: 0;
    }
  }

  .table-container {
    flex: 1;
    min-height: 0;
  }
}
</style>
